(() => {
  const register = () => {
    if (!window.Alpine || window.FAFGiftFinderRegistered) return;
    window.FAFGiftFinderRegistered = true;

    Alpine.data('fafGiftFinder', (el) => ({
      steps: ['recipient', 'type', 'lifestyle', 'subtype', 'budget'],
      urlStateParams: {
        recipient: 'gf_recipient',
        type: 'gf_type',
        lifestyle: 'gf_lifestyle',
        subtype: 'gf_subtype',
        budget: 'gf_budget',
      },
      step: 'recipient',
      loading: false,
      error: false,
      resultsHtml: '',
      resultCount: null,
      requestController: null,
      availabilityController: null,
      optionAvailability: {},
      refinements: {},
      refinementsOpen: false,
      answers: {
        recipient: null,
        type: null,
        lifestyle: null,
        subtype: null,
        budget: null,
      },

      init() {
        this.restoreStateFromUrl();
      },

      get collectionUrl() {
        return el.dataset.collectionUrl || '/collections/all';
      },

      get resultsSectionId() {
        return el.dataset.resultsSectionId || 'faf-gift-finder-results';
      },

      get countSectionId() {
        return el.dataset.countSectionId || 'faf-gift-finder-count';
      },

      get unavailableOptionLabel() {
        return el.dataset.unavailableOptionLabel || 'No gifts match this choice with your current selections';
      },

      get removeChoiceLabel() {
        return el.dataset.removeChoiceLabel || 'Remove and change choice';
      },

      choiceRemovalLabel(label) {
        return `${this.removeChoiceLabel}: ${label}`;
      },

      get hasAnswers() {
        return Object.values(this.answers).some(Boolean);
      },

      get subtypeRequired() {
        return ['Shoes', 'Boots'].includes(this.answers.type?.value || '');
      },

      get activeSteps() {
        return this.steps.filter(
          (group) => group !== 'subtype' || this.subtypeRequired
        );
      },

      get selectedAnswers() {
        return this.activeSteps
          .map((group) => {
            const answer = this.answers[group];
            return answer ? { group, label: answer.label } : null;
          })
          .filter(Boolean);
      },

      get progressPercent() {
        if (this.step === 'results') return 100;
        const steps = this.activeSteps;
        const index = steps.indexOf(this.step);
        return index < 0 ? 25 : ((index + 1) / steps.length) * 100;
      },

      subtypeOptionVisible(node) {
        const parentValue = node?.dataset?.giftParentValue || '';
        return Boolean(
          parentValue &&
          parentValue === (this.answers.type?.value || '')
        );
      },

      optionNodes(group) {
        if (!this.steps.includes(group)) return [];

        const nodes = Array.from(
          el.querySelectorAll(`[data-gift-group="${group}"]`)
        );

        if (group === 'subtype') {
          return nodes.filter((node) => this.subtypeOptionVisible(node));
        }

        return nodes;
      },

      answerFromNode(node) {
        return {
          optionId: node?.dataset?.giftOptionId || '',
          label: node?.dataset?.giftLabel || '',
          value: node?.dataset?.giftValue || '',
          min: node?.dataset?.priceMin || '',
          max: node?.dataset?.priceMax || '',
        };
      },

      syncStateToUrl() {
        const url = new URL(window.location.href);

        this.steps.forEach((group) => {
          const param = this.urlStateParams[group];
          const optionId =
            group === 'subtype' && !this.subtypeRequired
              ? ''
              : this.answers[group]?.optionId;

          if (optionId) {
            url.searchParams.set(param, optionId);
          } else {
            url.searchParams.delete(param);
          }
        });

        window.history.replaceState(
          window.history.state,
          '',
          `${url.pathname}${url.search}${url.hash}`
        );
      },

      restoreStateFromUrl() {
        const url = new URL(window.location.href);

        this.steps.forEach((group) => {
          const optionId = url.searchParams.get(this.urlStateParams[group]);
          if (!optionId) return;

          const node = this.optionNodes(group).find(
            (option) => option.dataset.giftOptionId === optionId
          );

          if (node) {
            this.answers[group] = this.answerFromNode(node);
          }
        });

        /*
        * Re-sync once after restoring.
        * This also cleans up a gf_* parameter if an option/block has
        * since been deleted or recreated in the theme editor.
        */
        if (!this.subtypeRequired) {
          this.answers.subtype = null;
        } else if (
          this.answers.subtype &&
          !this.optionNodes('subtype').some(
            (node) => node.dataset.giftOptionId === this.answers.subtype?.optionId
          )
        ) {
          this.answers.subtype = null;
        }

        this.syncStateToUrl();

        const firstUnanswered = this.activeSteps.find(
          (group) => !this.answers[group]
        );

        this.step = firstUnanswered || 'results';

        if (this.hasAnswers) {
          this.loadResults();
        }

        if (firstUnanswered) {
          this.clearAvailability(firstUnanswered);
          this.refreshAvailability(firstUnanswered);
        }
      },

      isSelected(node) {
        const group = node?.dataset?.giftGroup;
        const optionId = node?.dataset?.giftOptionId;
        return Boolean(group && optionId && this.answers[group]?.optionId === optionId);
      },

      isUnavailable(node) {
        const optionId = node?.dataset?.giftOptionId;
        return Boolean(optionId && this.optionAvailability[optionId] === false);
      },

      clearAvailability(group) {
        const next = { ...this.optionAvailability };
        this.optionNodes(group).forEach((node) => {
          delete next[node.dataset.giftOptionId];
        });
        this.optionAvailability = next;
      },

      refinementValues(param) {
        return Array.isArray(this.refinements[param]) ? this.refinements[param] : [];
      },

      refinementParts(value) {
        return String(value || '')
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);
      },

      selectedRefinementParts(param) {
        return this.refinementValues(param)
          .flatMap((value) => this.refinementParts(value));
      },

      isRefinementSelected(node) {
        const param = node?.dataset?.refinementParam || '';
        const value = node?.dataset?.refinementValue || '';
        const serverActive = node?.dataset?.refinementActive === 'true';

        if (!param || !value) return false;
        if (serverActive) return true;

        const targetParts = this.refinementParts(value);
        const selectedParts = new Set(this.selectedRefinementParts(param));

        return Boolean(
          targetParts.length &&
          targetParts.every((part) => selectedParts.has(part))
        );
      },

      toggleRefinement(node) {
        const param = node?.dataset?.refinementParam || '';
        const value = node?.dataset?.refinementValue || '';

        if (!param || !value || !this.hasAnswers || node?.disabled) return;

        const selected = new Set(this.refinementValues(param));
        const singleSelect =
          param === 'filter.p.m.faf.search_colour' ||
          param === 'filter.p.m.faf.gift_colour_family';

        const groupedToggle =
          param === 'filter.p.m.faf.gift_wrap_type';

        if (singleSelect) {
          if (selected.has(value)) {
            selected.clear();
          } else {
            selected.clear();
            selected.add(value);
          }
        } else if (groupedToggle) {
          const targetParts = this.refinementParts(value);
          const allSelected = this.isRefinementSelected(node);

          Array.from(selected).forEach((selectedValue) => {
            const parts = this.refinementParts(selectedValue);

            if (parts.some((part) => targetParts.includes(part))) {
              selected.delete(selectedValue);
            }
          });

          if (!allSelected) {
            selected.add(targetParts.join(','));
          }
        } else if (selected.has(value)) {
          selected.delete(value);
        } else {
          selected.add(value);
        }

        const next = { ...this.refinements };

        if (selected.size) {
          next[param] = Array.from(selected);
        } else {
          delete next[param];
        }

        this.refinements = next;
        this.refinementsOpen = true;
        this.loadResults();
      },

      removeRefinement(node) {
        const param = node?.dataset?.refinementParam || '';
        const value = node?.dataset?.refinementValue || '';

        if (!param || !value) return;

        const valueParts = String(value)
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);

        const current = this.refinementValues(param);
        const remaining = current.filter((selectedValue) => {
          const selectedParts = String(selectedValue)
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);

          return !valueParts.some((part) => selectedParts.includes(part));
        });

        if (remaining.length === current.length) return;

        const next = { ...this.refinements };

        if (remaining.length) {
          next[param] = remaining;
        } else {
          delete next[param];
        }

        this.refinements = next;
        this.refinementsOpen = true;

        if (this.hasAnswers) {
          this.loadResults();
        }
      },

    clearRefinements() {
      if (!Object.keys(this.refinements).length) return;

      this.refinements = {};
      this.refinementsOpen = true;

      if (this.hasAnswers) {
        this.loadResults();
      }
    },

      choose(node) {
        const group = node?.dataset?.giftGroup;
        if (!this.steps.includes(group) || this.isUnavailable(node)) return;

        const nextAnswer = this.answerFromNode(node);
        const answerChanged = this.answers[group]?.optionId !== nextAnswer.optionId;

        this.answers[group] = nextAnswer;

        if (group === 'type' && answerChanged) {
          this.answers.subtype = null;
        }

        if (answerChanged) {
          this.refinements = {};
          this.refinementsOpen = false;
        }

        this.syncStateToUrl();
        this.loadResults();

        const steps = this.activeSteps;
        const index = steps.indexOf(group);
        const nextStep =
          steps.slice(index + 1).find((stepName) => !this.answers[stepName]) ||
          'results';

        this.step = nextStep;

        if (nextStep === 'results') {
          this.revealResults();
          return;
        }

        this.clearAvailability(nextStep);
        this.focusCurrentStep();
        this.refreshAvailability(nextStep);
      },

      removeAnswer(group) {
        if (!this.steps.includes(group) || !this.answers[group]) return;

        this.answers[group] = null;

        if (group === 'type') {
          this.answers.subtype = null;
        }

        this.refinements = {};
        this.refinementsOpen = false;
        this.syncStateToUrl();
        this.step = group;
        this.clearAvailability(group);
        this.focusCurrentStep();
        this.refreshAvailability(group);

        if (this.hasAnswers) {
          this.loadResults();
        } else {
          this.requestController?.abort();
          this.requestController = null;
          this.loading = false;
          this.error = false;
          this.resultsHtml = '';
          this.resultCount = null;
        }
      },

      reset() {
        this.requestController?.abort();
        this.availabilityController?.abort();
        this.requestController = null;
        this.availabilityController = null;
        this.answers = {
          recipient: null,
          type: null,
          lifestyle: null,
          subtype: null,
          budget: null,
        };
        this.optionAvailability = {};
        this.refinements = {};
        this.refinementsOpen = false;
        this.syncStateToUrl();
        this.step = 'recipient';
        this.loading = false;
        this.error = false;
        this.resultsHtml = '';
        this.resultCount = null;
        this.focusCurrentStep();
      },

      showResults() {
        this.step = 'results';
        if (!this.resultsHtml && !this.loading) this.loadResults();
        this.revealResults();
      },

      focusCurrentStep() {
        this.$nextTick(() => {
          const panel = el.querySelector(`[data-gift-step="${this.step}"]`);
          panel?.querySelector('[data-gift-step-heading]')?.focus({
            preventScroll: true,
          });
        });
      },

      revealResults() {
        this.$nextTick(() => {
          const panel = this.$refs.resultsPanel;
          if (!panel) return;

          const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
          panel.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
          });

          const heading = panel.querySelector('[data-gift-results-heading]');
          const focusTarget = this.resultCount !== null ? heading : panel;
          focusTarget?.focus({ preventScroll: true });
        });
      },

      answersWithout(group) {
        return {
          ...this.answers,
          [group]: null,
        };
      },

      applyAnswerToUrl(url, group, answer) {
        if (!answer) return;

        if (group === 'recipient' && answer.value) {
          url.searchParams.set('filter.p.m.faf.new_gender', answer.value);
          return;
        }

        if (group === 'type' && answer.value) {
          url.searchParams.set('filter.p.m.faf.type', answer.value);
          return;
        }

        if (group === 'lifestyle' && answer.value) {
          url.searchParams.set('filter.p.m.faf.gift_use_case', answer.value);
          return;
        }

        if (group === 'subtype' && answer.value) {
          url.searchParams.set('filter.p.product_type', answer.value);
          return;
        }

        if (group === 'budget') {
          const min = Number(answer.min || 0);
          const max = Number(answer.max || 0);

          if (min > 0) url.searchParams.set('filter.v.price.gte', String(min));
          if (max > 0) url.searchParams.set('filter.v.price.lte', String(max));
        }
      },

      applyRefinementsToUrl(url) {
        Object.entries(this.refinements).forEach(([param, values]) => {
          if (!param || !Array.isArray(values) || !values.length) return;
          url.searchParams.set(param, values.join(','));
        });
      },

      buildUrl(options = {}) {
        const url = new URL(this.collectionUrl, window.location.origin);
        const answers = options.answers || this.answers;

        url.searchParams.set('filter.v.availability', '1');

        this.activeSteps.forEach((group) => {
          this.applyAnswerToUrl(url, group, answers[group]);
        });

        this.applyRefinementsToUrl(url);

        if (options.sectionId) {
          url.searchParams.set('section_id', options.sectionId);
        }

        return url;
      },

      get fullCollectionUrl() {
        return this.buildUrl().toString();
      },

      async fetchAvailabilitySnapshot(answers, signal) {
        const response = await fetch(
          this.buildUrl({ answers, sectionId: this.countSectionId }),
          {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Gift finder availability request failed: ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('[data-gift-finder-count]');

        if (!fragment) {
          throw new Error('Gift finder availability fragment was not returned.');
        }

        return {
          resultCount: Number(fragment.dataset.resultCount || 0),
        };
      },

      async probeGroupOptions(group, nodes, baseAnswers, signal) {
        const entries = await Promise.all(
          nodes.map(async (node) => {
            const answer = this.answerFromNode(node);
            const candidateAnswers = {
              ...baseAnswers,
              [group]: answer,
            };
            const snapshot = await this.fetchAvailabilitySnapshot(candidateAnswers, signal);
            return [answer.optionId, snapshot.resultCount > 0];
          })
        );

        return Object.fromEntries(entries);
      },

      async refreshAvailability(group) {
        if (!this.steps.includes(group)) return;

        this.availabilityController?.abort();
        const controller = new AbortController();
        this.availabilityController = controller;
        this.clearAvailability(group);

        const nodes = this.optionNodes(group);
        if (!nodes.length) return;

        const baseAnswers = this.answersWithout(group);

        try {
          const updates = await this.probeGroupOptions(
            group,
            nodes,
            baseAnswers,
            controller.signal
          );

          if (controller.signal.aborted) return;
          this.optionAvailability = {
            ...this.optionAvailability,
            ...updates,
          };
        } catch (error) {
          if (error?.name !== 'AbortError') {
            console.warn('Gift finder option availability could not be refreshed.', error);
          }
        } finally {
          if (this.availabilityController === controller) {
            this.availabilityController = null;
          }
        }
      },

      async loadResults() {
        if (!this.hasAnswers) return;

        this.requestController?.abort();
        const controller = new AbortController();
        this.requestController = controller;
        this.loading = true;
        this.error = false;
        this.resultCount = null;

        try {
          const response = await fetch(
            this.buildUrl({ sectionId: this.resultsSectionId }),
            {
              credentials: 'same-origin',
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
              signal: controller.signal,
            }
          );

          if (!response.ok) {
            throw new Error(`Gift finder request failed: ${response.status}`);
          }

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fragment = doc.querySelector('[data-gift-finder-results]');

          if (!fragment) throw new Error('Gift finder results fragment was not returned.');

          this.resultCount = Number(fragment.dataset.resultCount || 0);
          this.resultsHtml = fragment.outerHTML;

          this.$nextTick(() => {
            const holder = this.$refs.results;
            if (!holder) return;

            window.FAFSections?.enhance?.(holder, { source: 'gift-finder' });

            const cards = Array.from(
              holder.querySelectorAll('[data-elevar-product-card]')
            );

            window.FAFAnalytics?.viewItemListCards?.(cards, {
              list: 'gift_finder',
              startPosition: 0,
            });
          });
        } catch (error) {
          if (error?.name === 'AbortError') return;
          console.error(error);
          this.error = true;
        } finally {
          if (this.requestController === controller) {
            this.requestController = null;
            this.loading = false;
          }
        }
      },

      destroy() {
        this.requestController?.abort();
        this.availabilityController?.abort();
      },
    }));
  };

  if (window.Alpine) register();
  document.addEventListener('alpine:init', register, { once: true });
})();
